import { 
  Box, 
  VStack, 
  HStack, 
  Text, 
  Button, 
  Heading, 
  Container, 
  SimpleGrid, 
  Icon, 
  useColorModeValue,
  Badge,
  Divider,
  List,
  ListItem,
  ListIcon,
  useDisclosure,
  Alert,
  AlertIcon,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Table,
  Thead,
  Tr,
  Th,
  Tbody,
  Td,
  Input,
  FormControl,
  FormLabel,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  Switch,
  Textarea,
  useToast,
  IconButton,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  InputGroup,
  InputRightElement,
  Select
} from '@chakra-ui/react'
import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { FaCheck, FaCrown, FaUsers, FaMicrophone, FaQrcode, FaChartLine, FaEdit, FaTrash, FaPlus, FaSave, FaArrowLeft, FaSignOutAlt, FaEye, FaEyeSlash, FaSync, FaChartBar } from 'react-icons/fa'
import { secureAdminService } from '../services/secureAdminService'
import { pricingService } from '../services/firestore'
import { AdminSettings, SubscriptionPlan, Subscription, PromoCode } from '../types/user'
import { useAuthContext } from '../contexts/AuthContext'
import { PromoCodeAuditTrail } from './admin/PromoCodeAuditTrail'

interface PricingPackage {
  id: string
  firebaseId?: string
  name: string
  type: 'individual' | 'club'
  price: number
  priceType: 'per_month' | 'per_court_per_month' | 'one_time'
  features: string[]
  limitations: string[]
  isActive: boolean
  description: string
  maxDevices?: number
  maxCourts?: number
  courts?: number
}

interface Member {
  id: string
  name: string
  email: string
  package: string
  status: 'active' | 'inactive' | 'pending'
  joinDate: string
  lastActive: string
  courts?: number
  isAdmin?: boolean
}

export function AdminPage() {
  const navigate = useNavigate()
  const toast = useToast()
  const { user, isAuthenticated: authContextAuthenticated, loading: authLoading } = useAuthContext()
  const bgColor = useColorModeValue('#09090b', '#09090b')
  const textColor = useColorModeValue('white', 'white')
  const cardBg = useColorModeValue('#191919', '#191919')
  const accentColor = '#3182ce'

  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [username, setUsername] = useState('')
  const [showAdminLogin, setShowAdminLogin] = useState(false)
  const [adminSessionInfo, setAdminSessionInfo] = useState<{ isAuthenticated: boolean; expiresAt: number | null }>({ isAuthenticated: false, expiresAt: null })

  // Firestore data
  const [adminSettings, setAdminSettings] = useState<AdminSettings | null>(null)
  const [pricingPlans, setPricingPlans] = useState<SubscriptionPlan[]>([])
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([])
  const [pricingPackages, setPricingPackages] = useState<PricingPackage[]>([])
  const [members, setMembers] = useState<Member[]>([])
  const [promoCodes, setPromoCodes] = useState<PromoCode[]>([])

  const [editingPackage, setEditingPackage] = useState<PricingPackage | null>(null)
  const [editingMember, setEditingMember] = useState<Member | null>(null)
  const [editingPromoCode, setEditingPromoCode] = useState<PromoCode | null>(null)
  const [exchangeRate, setExchangeRate] = useState(() => {
    return localStorage.getItem('exchangeRate') || '18.50'
  })

  // Load admin data securely
  const loadAdminData = async () => {
    try {
      // Load admin settings
      const settings = await secureAdminService.getAdminSettings()
      setAdminSettings(settings)
      
      // Set exchange rate from admin settings
      if (settings?.exchangeRate) {
        setExchangeRate(settings.exchangeRate)
      }

      // Load pricing plans
      const plans = await secureAdminService.getPricingPlans()
      setPricingPlans(plans)

      // Load subscriptions
      const subs = await secureAdminService.getAllSubscriptions()
      setSubscriptions(subs)

      // Load members (users with subscription data)
      const memberData = await secureAdminService.getMembers()
      setMembers(memberData)

      // Load promo codes
      const promoData = await secureAdminService.getPromoCodes()
      setPromoCodes(promoData)

      // Convert pricing plans to packages format for UI
      const packages = plans.map((plan: any) => ({
        id: plan.id || plan.type, // Use Firebase document ID if available, fallback to type
        firebaseId: plan.id, // Store the actual Firebase document ID
        name: plan.name?.toUpperCase() || 'UNNAMED PLAN',
        type: plan.type,
        price: plan.price / 100,
        priceType: plan.type === 'individual' ? 'per_month' as const : 'per_court_per_month' as const,
        features: plan.features,
        limitations: plan.limitations,
        isActive: true,
        description: plan.description,
        maxDevices: plan.type === 'individual' ? 2 : undefined,
        maxCourts: plan.courts,
        courts: plan.courts
      }))
      setPricingPackages(packages)
      
    } catch (error) {
      console.error('Error loading admin data:', error)
      console.error('Error details:', {
        message: error instanceof Error ? error.message : 'Unknown error',
        stack: error instanceof Error ? error.stack : undefined,
        name: error instanceof Error ? error.name : 'Unknown'
      })
      toast({
        title: 'Error',
        description: `Failed to load data from database: ${error instanceof Error ? error.message : 'Unknown error'}`,
        status: 'error',
        duration: 5000,
        isClosable: true,
      })
    }
  }

  // Check if user has valid admin session
  const checkAdminSession = async (): Promise<boolean> => {
    try {
      const isValid = await secureAdminService.validateSession()
      if (isValid) {
        const sessionInfo = secureAdminService.getSessionInfo()
        setAdminSessionInfo(sessionInfo)
        setUsername('Admin') // We don't expose the actual username for security
        return true
      }
      return false
    } catch (error) {
      return false
    }
  }

  // Check authentication on component mount
  useEffect(() => {
    const checkAuth = async () => {
      if (authLoading) {
        return
      }
      
      // Check if user has valid admin session
      const hasValidSession = await checkAdminSession()
      
      if (hasValidSession) {
        setIsAuthenticated(true)
        setIsLoading(false)
        loadAdminData()
      } else {
        setIsLoading(false)
        setShowAdminLogin(true)
      }
    }

    checkAuth()
  }, [authLoading])

  const handleLogout = async () => {
    await secureAdminService.logout()
    setIsAuthenticated(false)
    setShowAdminLogin(false)
    toast({
      title: 'Logged out',
      description: 'You have been successfully logged out',
      status: 'info',
      duration: 3000,
      isClosable: true,
    })
    // Navigate back to landing page
    navigate('/')
  }

  const handleBackToLanding = () => {
    navigate('/')
  }

  const handleAdminLoginSuccess = () => {
    setIsAuthenticated(true)
    setShowAdminLogin(false)
    setUsername('Admin')
    loadAdminData()
    toast({
      title: 'Access granted',
      description: 'Welcome, Admin',
      status: 'success',
      duration: 2000,
      isClosable: true,
    })
  }

  const handleSavePackage = async (packageData: PricingPackage) => {
    try {
      console.log('Saving package data:', packageData)
      console.log('Editing package:', editingPackage)
      
      // Convert to SubscriptionPlan format for Firebase
      const planData = {
        type: packageData.type,
        name: packageData.name,
        description: packageData.description || '',
        price: packageData.price * 100, // Convert to cents for Firebase
        courts: packageData.courts,
        features: packageData.features,
        limitations: []
      }
      


      if (editingPackage && editingPackage.firebaseId) {
        // Update existing plan using Firebase document ID
        await secureAdminService.updatePricingPlan(editingPackage.firebaseId, planData)
        setPricingPackages(packages => 
          packages.map(p => p.id === packageData.id ? { ...packageData, firebaseId: editingPackage.firebaseId } : p)
        )
      } else {
        // Create new plan
        const newPlanId = await secureAdminService.createPricingPlan(planData)
        const newPackage = { ...packageData, id: newPlanId, firebaseId: newPlanId }
        setPricingPackages(packages => [...packages, newPackage])
      }
      
      setEditingPackage(null)
      toast({
        title: 'Package saved',
        description: 'Pricing package updated successfully in Firebase',
        status: 'success',
        duration: 3000,
        isClosable: true,
      })
    } catch (error) {
      console.error('Error saving package:', error)
      console.error('Error details:', {
        message: error instanceof Error ? error.message : 'Unknown error',
        stack: error instanceof Error ? error.stack : undefined
      })
      toast({
        title: 'Error',
        description: `Failed to save package to Firebase: ${error instanceof Error ? error.message : 'Unknown error'}`,
        status: 'error',
        duration: 5000,
        isClosable: true,
      })
    }
  }

  const handleSaveMember = (memberData: Member) => {
    if (editingMember) {
      setMembers(members => 
        members.map(m => m.id === memberData.id ? memberData : m)
      )
    } else {
      setMembers(members => [...members, { ...memberData, id: Date.now().toString() }])
    }
    setEditingMember(null)
    toast({
      title: 'Member saved',
      description: 'Member information updated successfully',
      status: 'success',
      duration: 3000,
      isClosable: true,
    })
  }

  const handleDeletePackage = async (packageId: string) => {
    try {
      // Find the package to get its Firebase ID
      const packageToDelete = pricingPackages.find(p => p.id === packageId)
      if (!packageToDelete?.firebaseId) {
        throw new Error('Package not found or missing Firebase ID')
      }
      
      await secureAdminService.deletePricingPlan(packageToDelete.firebaseId)
      setPricingPackages(packages => packages.filter(p => p.id !== packageId))
      toast({
        title: 'Package deleted',
        description: 'Pricing package removed successfully from Firebase',
        status: 'success',
        duration: 3000,
        isClosable: true,
      })
    } catch (error) {
      console.error('Error deleting package:', error)
      toast({
        title: 'Error',
        description: 'Failed to delete package from Firebase',
        status: 'error',
        duration: 5000,
        isClosable: true,
      })
    }
  }

  const handleDeleteMember = async (memberId: string) => {
    try {
      // Find the member to get the userId
      const memberToDelete = members.find(m => m.id === memberId)
      if (!memberToDelete?.id) {
        throw new Error('Member not found or missing user ID')
      }
      
      // Delete user from Firebase Authentication and Firestore
      // The member.id IS the userId
      await secureAdminService.deleteUser(memberToDelete.id)
      
      // Remove from local state
      setMembers(members => members.filter(m => m.id !== memberId))
      
      toast({
        title: 'Member deleted',
        description: 'User deleted successfully from Firebase Authentication and Firestore',
        status: 'success',
        duration: 3000,
        isClosable: true,
      })
    } catch (error) {
      console.error('Error deleting member:', error)
      toast({
        title: 'Error',
        description: 'Failed to delete user from Firebase',
        status: 'error',
        duration: 5000,
        isClosable: true,
      })
    }
  }

  const handleSavePromoCode = async (promoData: PromoCode) => {
    try {
      if (editingPromoCode && editingPromoCode.id) {
        // Update existing promo code
        await secureAdminService.updatePromoCode(editingPromoCode.id, promoData)
        setPromoCodes(codes => 
          codes.map(p => p.id === promoData.id ? promoData : p)
        )
      } else {
        // Create new promo code
        const newPromoId = await secureAdminService.createPromoCode(promoData)
        const newPromo = { ...promoData, id: newPromoId }
        setPromoCodes(codes => [...codes, newPromo])
      }
      
      setEditingPromoCode(null)
      toast({
        title: 'Promo code saved',
        description: 'Promo code updated successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
      })
    } catch (error) {
      console.error('Error saving promo code:', error)
      toast({
        title: 'Error',
        description: 'Failed to save promo code',
        status: 'error',
        duration: 5000,
        isClosable: true,
      })
    }
  }

  const handleDeletePromoCode = async (promoId: string) => {
    try {
      await secureAdminService.deletePromoCode(promoId)
      setPromoCodes(codes => codes.filter(p => p.id !== promoId))
      toast({
        title: 'Promo code deleted',
        description: 'Promo code removed successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
      })
    } catch (error) {
      console.error('Error deleting promo code:', error)
      toast({
        title: 'Error',
        description: 'Failed to delete promo code',
        status: 'error',
        duration: 5000,
        isClosable: true,
      })
    }
  }

  if (isLoading) {
    return (
      <Box minH="100vh" bg={bgColor} color={textColor} display="flex" alignItems="center" justifyContent="center">
        <Text>Loading...</Text>
      </Box>
    )
  }

  if (!isAuthenticated) {
    return (
      <Box minH="100vh" bg={bgColor} color={textColor} display="flex" alignItems="center" justifyContent="center">
        <AdminLoginModal 
          isOpen={showAdminLogin}
          onClose={handleBackToLanding}
          onSuccess={handleAdminLoginSuccess}
        />
      </Box>
    )
  }

  return (
    <Box minH="100vh" bg={bgColor} color={textColor}>
      {/* Header */}
      <Box bg={cardBg} borderBottom="1px solid" borderColor="gray.700" px={6} py={4}>
        <HStack justify="space-between">
          <HStack spacing={4}>
            <IconButton
              aria-label="Back to pricing"
              icon={<FaArrowLeft />}
              onClick={handleBackToLanding}
              variant="ghost"
              color="white"
              _hover={{ bg: 'rgba(255, 255, 255, 0.1)' }}
            />
            <VStack align="start" spacing={0}>
              <Heading size="lg" color={textColor}>
                Admin Management
              </Heading>
              <Text fontSize="sm" color="gray.400">
                Welcome, {username}
              </Text>
            </VStack>
          </HStack>
          <HStack spacing={4}>
            <IconButton
              aria-label="Refresh data"
              icon={<FaSync />}
              onClick={loadAdminData}
              variant="ghost"
              color="gray.400"
              _hover={{ color: 'white' }}
            />
            <Button
              leftIcon={<FaSignOutAlt />}
              variant="ghost"
              color="gray.400"
              _hover={{ color: 'white' }}
              onClick={handleLogout}
            >
              Logout
            </Button>
          </HStack>
        </HStack>
      </Box>

      {/* Main Content */}
      <Container maxW="container.xl" py={8}>
        <Tabs variant="soft-rounded" colorScheme="blue">
          <TabList justifyContent="center" mb={8}>
            <Tab>Pricing Packages</Tab>
            <Tab>Members</Tab>
            <Tab>Admin Settings</Tab>
            <Tab>Promo Setup</Tab>
            <Tab>Promo Audit</Tab>
          </TabList>

          <TabPanels>
            {/* Pricing Packages Tab */}
            <TabPanel>
              <VStack spacing={6}>
                <HStack justify="space-between" w="full">
                  <Heading size="md" color={accentColor}>
                    Pricing Packages
                  </Heading>
                  <Button
                    leftIcon={<FaPlus />}
                    colorScheme="blue"
                    onClick={() => setEditingPackage({} as PricingPackage)}
                  >
                    Add Package
                  </Button>
                </HStack>

                <FormControl maxW="300px">
                  <FormLabel fontSize="sm" color="gray.300">Exchange Rate (USD to ZAR)</FormLabel>
                  <HStack>
                    <Input
                      value={exchangeRate}
                      onChange={(e) => setExchangeRate(e.target.value)}
                      placeholder="18.50"
                      size="sm"
                      bg="#191919"
                      color="white"
                      borderColor="gray.600"
                      _placeholder={{ color: 'gray.400' }}
                    />
                    <Button
                      size="sm"
                      colorScheme="blue"
                      onClick={async () => {
                        try {
                          // Save to backend
                          await secureAdminService.updateAdminSettings({
                            exchangeRate: exchangeRate
                          })
                          // Also save to localStorage as backup
                          localStorage.setItem('exchangeRate', exchangeRate)
                          // Update local state
                          setAdminSettings(prev => prev ? { ...prev, exchangeRate: exchangeRate } : null)
                          toast({
                            title: 'Exchange Rate Updated',
                            description: `Exchange rate saved as ${exchangeRate}`,
                            status: 'success',
                            duration: 3000,
                            isClosable: true,
                          })
                        } catch (error) {
                          console.error('Failed to update exchange rate:', error)
                          toast({
                            title: 'Update Failed',
                            description: 'Failed to save exchange rate. Please try again.',
                            status: 'error',
                            duration: 3000,
                            isClosable: true,
                          })
                        }
                      }}
                    >
                      Update
                    </Button>
                  </HStack>
                </FormControl>

                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6} w="full">
                  {pricingPackages.map((pkg) => (
                    <Box
                      key={pkg.id}
                      bg={cardBg}
                      p={6}
                      borderRadius="xl"
                      border="1px solid"
                      borderColor="gray.600"
                      position="relative"
                    >
                      <VStack spacing={4} align="stretch">
                        <HStack justify="space-between">
                          <Badge colorScheme={pkg.type === 'club' ? 'blue' : 'green'} fontSize="lg" px={4} py={2}>
                            {pkg.name}
                          </Badge>
                          <HStack spacing={2}>
                            <Button
                              size="sm"
                              variant="ghost"
                              color="blue.400"
                              onClick={() => setEditingPackage(pkg)}
                            >
                              <FaEdit />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              color="red.400"
                              onClick={() => handleDeletePackage(pkg.id)}
                            >
                              <FaTrash />
                            </Button>
                          </HStack>
                        </HStack>

                        <Box textAlign="center">
                          <Text fontSize="2xl" fontWeight="bold" color={accentColor}>
                            ZAR {pkg.price.toLocaleString()}
                          </Text>
                          <Text fontSize="sm" color="gray.400">
                            {pkg.priceType === 'per_month' && '/month'}
                            {pkg.priceType === 'per_court_per_month' && '/court per month'}
                            {pkg.priceType === 'one_time' && 'one time'}
                          </Text>
                          {pkg.priceType === 'per_court_per_month' && pkg.courts && pkg.courts > 1 && (
                            <Text fontSize="xs" color="gray.500" mt={1}>
                              Total: ZAR {(pkg.price * pkg.courts).toLocaleString()}/month
                            </Text>
                          )}
                        </Box>

                        <Divider />

                        <VStack spacing={3} align="stretch">
                          <Text fontWeight="bold" fontSize="sm" color="gray.300">
                            Features:
                          </Text>
                          <List spacing={2}>
                            {pkg.features.map((feature, index) => (
                              <ListItem key={index} fontSize="sm">
                                <ListIcon as={FaCheck} color="green.400" />
                                {feature}
                              </ListItem>
                            ))}
                          </List>


                        </VStack>

                        <HStack justify="space-between">
                          <Text fontSize="sm" color="gray.400">
                            Status: {pkg.isActive ? 'Active' : 'Inactive'}
                          </Text>
                          <Switch
                            isChecked={pkg.isActive}
                            onChange={(e) => {
                              setPricingPackages(packages =>
                                packages.map(p => p.id === pkg.id ? { ...p, isActive: e.target.checked } : p)
                              )
                            }}
                            colorScheme="green"
                          />
                        </HStack>
                      </VStack>
                    </Box>
                  ))}
                </SimpleGrid>
              </VStack>
            </TabPanel>

            {/* Members Tab */}
            <TabPanel>
              <VStack spacing={6}>
                <HStack justify="space-between" w="full">
                  <Heading size="md" color={accentColor}>
                    Members
                  </Heading>
                  <Button
                    leftIcon={<FaPlus />}
                    colorScheme="blue"
                    onClick={() => setEditingMember({} as Member)}
                  >
                    Add Member
                  </Button>
                </HStack>

                <Box w="full" overflowX="auto">
                  <Table variant="simple" size="sm">
                    <Thead>
                      <Tr>
                        <Th color="gray.300">Name</Th>
                        <Th color="gray.300">Email</Th>
                        <Th color="gray.300">Package</Th>
                        <Th color="gray.300">Status</Th>
                        <Th color="gray.300">Join Date</Th>
                        <Th color="gray.300">Last Active</Th>
                        <Th color="gray.300">Actions</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {members.map((member) => (
                        <Tr key={member.id}>
                          <Td>
                            <HStack spacing={2}>
                              <Text>{member.name}</Text>
                              {(member.email === 'wade@seraphimfs.com' || member.isAdmin) && (
                                <Badge colorScheme="purple" size="sm">
                                  ADMIN
                                </Badge>
                              )}
                            </HStack>
                          </Td>
                          <Td>{member.email}</Td>
                          <Td>
                            <Badge colorScheme={member.package === 'club' ? 'blue' : 'green'}>
                              {member.package}
                            </Badge>
                          </Td>
                          <Td>
                            <Badge
                              colorScheme={
                                member.status === 'active' ? 'green' :
                                member.status === 'inactive' ? 'red' : 'yellow'
                              }
                            >
                              {member.status}
                            </Badge>
                          </Td>
                          <Td>{member.joinDate}</Td>
                          <Td>{member.lastActive}</Td>
                          <Td>
                            <HStack spacing={2}>
                              <Button
                                size="sm"
                                variant="ghost"
                                color="blue.400"
                                onClick={() => setEditingMember(member)}
                              >
                                <FaEdit />
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                color="red.400"
                                onClick={() => handleDeleteMember(member.id)}
                                title="Delete member"
                              >
                                <FaTrash />
                              </Button>
                            </HStack>
                          </Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                </Box>
              </VStack>
            </TabPanel>

            {/* Settings Tab */}
            <TabPanel>
              <VStack spacing={6}>
                <Heading size="md" color={accentColor}>
                  Admin Settings
                </Heading>

                <Box w="full" bg={cardBg} p={6} borderRadius="xl" border="1px solid" borderColor="gray.600">
                  <VStack spacing={6} align="stretch">
                    <HStack justify="space-between">
                      <VStack align="start" spacing={1}>
                        <Text fontWeight="bold" color="white">
                          Paywall System
                        </Text>
                        <Text fontSize="sm" color="gray.400">
                          Enable or disable the paywall system for all users
                        </Text>
                      </VStack>
                      <HStack spacing={2}>
                        <Button
                          size="sm"
                          colorScheme={adminSettings?.paywallEnabled ? "green" : "gray"}
                          variant={adminSettings?.paywallEnabled ? "solid" : "outline"}
                          onClick={async () => {
                            try {
                              await secureAdminService.updateAdminSettings({
                                paywallEnabled: true
                              })
                              setAdminSettings(prev => prev ? { ...prev, paywallEnabled: true } : null)
                              toast({
                                title: 'Paywall Enabled',
                                description: 'Paywall system is now active for all users',
                                status: 'success',
                                duration: 3000,
                                isClosable: true,
                              })
                            } catch (error) {
                              toast({
                                title: 'Error',
                                description: 'Failed to enable paywall system',
                                status: 'error',
                                duration: 5000,
                                isClosable: true,
                              })
                            }
                          }}
                        >
                          On
                        </Button>
                        <Button
                          size="sm"
                          colorScheme={!adminSettings?.paywallEnabled ? "red" : "gray"}
                          variant={!adminSettings?.paywallEnabled ? "solid" : "outline"}
                          onClick={async () => {
                            try {
                              await secureAdminService.updateAdminSettings({
                                paywallEnabled: false
                              })
                              setAdminSettings(prev => prev ? { ...prev, paywallEnabled: false } : null)
                              toast({
                                title: 'Paywall Disabled',
                                description: 'Paywall system is now inactive for all users',
                                status: 'success',
                                duration: 3000,
                                isClosable: true,
                              })
                            } catch (error) {
                              toast({
                                title: 'Error',
                                description: 'Failed to disable paywall system',
                                status: 'error',
                                duration: 5000,
                                isClosable: true,
                              })
                            }
                          }}
                        >
                          Off
                        </Button>
                      </HStack>
                    </HStack>

                    <Divider />

                    <HStack justify="space-between">
                      <VStack align="start" spacing={1}>
                        <Text fontWeight="bold" color="white">
                          Trial Duration
                        </Text>
                        <Text fontSize="sm" color="gray.400">
                          Number of days for free trial period
                        </Text>
                      </VStack>
                      <HStack spacing={2}>
                        <NumberInput
                          value={adminSettings?.trialDurationDays ?? 60}
                          onChange={(_, value) => {
                            setAdminSettings(prev => prev ? { ...prev, trialDurationDays: value } : null)
                          }}
                          min={1}
                          max={365}
                          w="120px"
                        >
                          <NumberInputField bg="#191919" color="white" borderColor="gray.600" />
                          <NumberInputStepper>
                            <NumberIncrementStepper />
                            <NumberDecrementStepper />
                          </NumberInputStepper>
                        </NumberInput>
                        <Button
                          size="sm"
                          colorScheme="blue"
                          onClick={async () => {
                            try {
                              await secureAdminService.updateAdminSettings({
                                trialDurationDays: adminSettings?.trialDurationDays ?? 60
                              })
                              toast({
                                title: 'Trial Duration Updated',
                                description: `Trial duration set to ${adminSettings?.trialDurationDays ?? 60} days`,
                                status: 'success',
                                duration: 3000,
                                isClosable: true,
                              })
                            } catch (error) {
                              toast({
                                title: 'Error',
                                description: 'Failed to update trial duration',
                                status: 'error',
                                duration: 5000,
                                isClosable: true,
                              })
                            }
                          }}
                        >
                          Save
                        </Button>
                      </HStack>
                    </HStack>

                    <Divider />

                    <HStack justify="space-between">
                      <VStack align="start" spacing={1}>
                        <Text fontWeight="bold" color="white">
                          Allow Individual users to display Club QR Code
                        </Text>
                        <Text fontSize="sm" color="gray.400">
                          When OFF, Individual package users cannot use the Club QR Code toggle on Match Setup
                        </Text>
                      </VStack>
                      <HStack spacing={2}>
                        <Button
                          size="sm"
                          colorScheme={adminSettings?.allowIndividualClubQRCode ? "green" : "gray"}
                          variant={adminSettings?.allowIndividualClubQRCode ? "solid" : "outline"}
                          onClick={async () => {
                            try {
                              await secureAdminService.updateAdminSettings({
                                allowIndividualClubQRCode: true
                              })
                              setAdminSettings(prev => prev ? { ...prev, allowIndividualClubQRCode: true } : null)
                              toast({
                                title: 'Setting updated',
                                description: 'Club QR Code toggle for Individuals is now enabled',
                                status: 'success',
                                duration: 3000,
                                isClosable: true,
                              })
                            } catch (error) {
                              toast({
                                title: 'Error',
                                description: 'Failed to enable Club QR Code toggle setting',
                                status: 'error',
                                duration: 5000,
                                isClosable: true,
                              })
                            }
                          }}
                        >
                          On
                        </Button>
                        <Button
                          size="sm"
                          colorScheme={!adminSettings?.allowIndividualClubQRCode ? "red" : "gray"}
                          variant={!adminSettings?.allowIndividualClubQRCode ? "solid" : "outline"}
                          onClick={async () => {
                            try {
                              await secureAdminService.updateAdminSettings({
                                allowIndividualClubQRCode: false
                              })
                              setAdminSettings(prev => prev ? { ...prev, allowIndividualClubQRCode: false } : null)
                              toast({
                                title: 'Setting updated',
                                description: 'Club QR Code toggle for Individuals is now disabled',
                                status: 'success',
                                duration: 3000,
                                isClosable: true,
                              })
                            } catch (error) {
                              toast({
                                title: 'Error',
                                description: 'Failed to disable Club QR Code toggle setting',
                                status: 'error',
                                duration: 5000,
                                isClosable: true,
                              })
                            }
                          }}
                        >
                          Off
                        </Button>
                      </HStack>
                    </HStack>

                    <Divider />

                    {/* Invoice Settings */}
                    <VStack spacing={4} align="stretch">
                      <Text fontWeight="bold" color="white">
                        Company Invoice Details
                      </Text>
                      
                      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                        <FormControl>
                          <FormLabel fontSize="sm" color="gray.300">Trading Name</FormLabel>
                          <Input
                            value={adminSettings?.tradingName || ''}
                            onChange={(e) => {
                              setAdminSettings(prev => prev ? { ...prev, tradingName: e.target.value } : null)
                            }}
                            bg="#191919"
                            borderColor="gray.600"
                            color="white"
                            _focus={{ borderColor: 'white', boxShadow: 'none' }}
                          />
                        </FormControl>

                        <FormControl>
                          <FormLabel fontSize="sm" color="gray.300">Email</FormLabel>
                          <Input
                            value={adminSettings?.email || ''}
                            onChange={(e) => {
                              setAdminSettings(prev => prev ? { ...prev, email: e.target.value } : null)
                            }}
                            bg="#191919"
                            borderColor="gray.600"
                            color="white"
                            _focus={{ borderColor: 'white', boxShadow: 'none' }}
                          />
                        </FormControl>

                        <FormControl>
                          <FormLabel fontSize="sm" color="gray.300">VAT Number</FormLabel>
                          <Input
                            value={adminSettings?.vat || ''}
                            onChange={(e) => {
                              setAdminSettings(prev => prev ? { ...prev, vat: e.target.value } : null)
                            }}
                            bg="#191919"
                            borderColor="gray.600"
                            color="white"
                            _focus={{ borderColor: 'white', boxShadow: 'none' }}
                          />
                        </FormControl>
                      </SimpleGrid>

                      <FormControl>
                        <FormLabel fontSize="sm" color="gray.300">Address</FormLabel>
                        <Textarea
                          value={adminSettings?.address || ''}
                          onChange={(e) => {
                            setAdminSettings(prev => prev ? { ...prev, address: e.target.value } : null)
                          }}
                          bg="#191919"
                          borderColor="gray.600"
                          color="white"
                          _focus={{ borderColor: 'white', boxShadow: 'none' }}
                          rows={3}
                        />
                      </FormControl>

                      <Button
                        colorScheme="blue"
                        onClick={async () => {
                          try {
                            await secureAdminService.updateAdminSettings({
                              tradingName: adminSettings?.tradingName || '',
                              address: adminSettings?.address || '',
                              email: adminSettings?.email || '',
                              vat: adminSettings?.vat || ''
                            })
                            toast({
                              title: 'Invoice settings updated',
                              status: 'success',
                              duration: 3000,
                              isClosable: true,
                            })
                          } catch (error) {
                            toast({
                              title: 'Error',
                              description: 'Failed to update invoice settings',
                              status: 'error',
                              duration: 4000,
                              isClosable: true,
                            })
                          }
                        }}
                        leftIcon={<FaSave />}
                      >
                        Save Invoice Settings
                      </Button>
                    </VStack>
                  </VStack>
                </Box>


              </VStack>
            </TabPanel>

            {/* Promo Codes Tab */}
            <TabPanel>
              <VStack spacing={6}>
                <HStack justify="space-between" w="full">
                  <Heading size="md" color={accentColor}>
                    Promo Codes
                  </Heading>
                  <Button
                    leftIcon={<FaPlus />}
                    colorScheme="blue"
                    onClick={() => setEditingPromoCode({
                      code: '',
                      discountPercentage: 0,
                      isActive: true,
                      usedCount: 0,
                      monthlyPayments: 0
                    } as PromoCode)}
                  >
                    Add Promo Code
                  </Button>
                </HStack>

                <Box w="full" overflowX="auto">
                  <Table variant="simple" size="sm">
                    <Thead>
                      <Tr>
                        <Th color="gray.300">Code</Th>
                        <Th color="gray.300">Discount</Th>
                        <Th color="gray.300">Used</Th>
                        <Th color="gray.300">Max Uses</Th>
                        <Th color="gray.300">Monthly Payments</Th>
                        <Th color="gray.300">Status</Th>
                        <Th color="gray.300">Actions</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {promoCodes.map((promo) => (
                        <Tr key={promo.id} _hover={{ bg: 'gray.800' }}>
                          <Td>
                            <Badge colorScheme={promo.isActive ? 'green' : 'red'} fontSize="sm">
                              {promo.code}
                            </Badge>
                          </Td>
                          <Td>
                            <Text fontWeight="bold" color={accentColor}>
                              {promo.discountPercentage}% OFF
                            </Text>
                          </Td>
                          <Td>
                            <Text fontSize="sm" color="gray.300">
                              {promo.usedCount} times
                            </Text>
                          </Td>
                          <Td>
                            <Text fontSize="sm" color="gray.300">
                              {promo.maxUses ? promo.maxUses : '∞'}
                            </Text>
                          </Td>
                          <Td>
                            <Text fontSize="sm" color="gray.300">
                              {(promo.monthlyPayments ?? 0) === 0 ? 'Unlimited' : `${promo.monthlyPayments ?? 0} month${(promo.monthlyPayments ?? 0) > 1 ? 's' : ''}`}
                            </Text>
                          </Td>
                          <Td>
                            <HStack spacing={2}>
                              <Badge colorScheme={promo.isActive ? 'green' : 'red'} size="sm">
                                {promo.isActive ? 'Active' : 'Inactive'}
                              </Badge>
                              <Switch
                                size="sm"
                                isChecked={promo.isActive}
                                onChange={(e) => {
                                  setPromoCodes(codes =>
                                    codes.map(p => p.id === promo.id ? { ...p, isActive: e.target.checked } : p)
                                  )
                                }}
                                colorScheme="green"
                              />
                            </HStack>
                          </Td>
                          <Td>
                            <HStack spacing={1}>
                              <IconButton
                                size="sm"
                                variant="ghost"
                                color="blue.400"
                                icon={<FaEdit />}
                                aria-label="Edit promo code"
                                onClick={() => setEditingPromoCode(promo)}
                              />
                              <IconButton
                                size="sm"
                                variant="ghost"
                                color="red.400"
                                icon={<FaTrash />}
                                aria-label="Delete promo code"
                                onClick={() => handleDeletePromoCode(promo.id!)}
                              />
                            </HStack>
                          </Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                </Box>
              </VStack>
            </TabPanel>

            {/* Promo Audit Trail Tab */}
            <TabPanel>
              <PromoCodeAuditTrail />
            </TabPanel>
          </TabPanels>
        </Tabs>
      </Container>

      {/* Package Edit Modal */}
      {editingPackage && (
        <PackageEditModal
          package={editingPackage}
          onSave={handleSavePackage}
          onCancel={() => setEditingPackage(null)}
        />
      )}

      {/* Member Edit Modal */}
      {editingMember && (
        <MemberEditModal
          member={editingMember}
          packages={pricingPackages}
          onSave={handleSaveMember}
          onCancel={() => setEditingMember(null)}
        />
      )}

      {/* Promo Code Edit Modal */}
      {editingPromoCode && (
        <PromoCodeEditModal
          promoCode={editingPromoCode}
          onSave={handleSavePromoCode}
          onCancel={() => setEditingPromoCode(null)}
        />
      )}
    </Box>
  )
}

// Package Edit Modal Component
function PackageEditModal({ package: pkg, onSave, onCancel }: {
  package: PricingPackage
  onSave: (pkg: PricingPackage) => void
  onCancel: () => void
}) {
  const [formData, setFormData] = useState<PricingPackage>(pkg)
  const [features, setFeatures] = useState(pkg.features?.join('\n') || '')

  const handleSave = () => {
    onSave({
      ...formData,
      features: features.split('\n').filter(f => f.trim()),
      limitations: []
    })
  }

  return (
    <Modal isOpen={true} onClose={onCancel} size="lg">
      <ModalOverlay />
      <ModalContent bg="#191919" color="white">
        <ModalHeader>Edit Package</ModalHeader>
        <ModalBody>
          <VStack spacing={4}>
            <FormControl>
              <FormLabel>Name</FormLabel>
              <Input
                value={formData.name || ''}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Package name"
              />
            </FormControl>

            <FormControl>
              <FormLabel>Type</FormLabel>
              <Input
                value={formData.type || ''}
                onChange={(e) => setFormData({ ...formData, type: e.target.value as 'individual' | 'club' })}
                placeholder="individual or club"
              />
            </FormControl>

            <FormControl>
              <FormLabel>Price (ZAR)</FormLabel>
              <InputGroup>
                <NumberInput value={formData.price || 0} onChange={(_, value) => setFormData({ ...formData, price: value })}>
                  <NumberInputField />
                  <NumberInputStepper>
                    <NumberIncrementStepper />
                    <NumberDecrementStepper />
                  </NumberInputStepper>
                </NumberInput>
                <InputRightElement>
                  <Text color="gray.400">R</Text>
                </InputRightElement>
              </InputGroup>
            </FormControl>

            <FormControl>
              <FormLabel>Price Type</FormLabel>
              <Select
                value={formData.priceType || ''}
                onChange={(e) => setFormData({ ...formData, priceType: e.target.value as any })}
                placeholder="Select price type"
                color="black"
                bg="white"
              >
                <option value="per_month">Per Month</option>
                <option value="per_court_per_month">Per Court Per Month</option>
                <option value="one_time">One Time</option>
              </Select>
            </FormControl>

            <FormControl>
              <FormLabel>Number of Courts</FormLabel>
              <NumberInput 
                value={formData.courts || (formData.priceType === 'per_court_per_month' ? 1 : 0)} 
                onChange={(_, value) => setFormData({ ...formData, courts: value })}
                min={0}
              >
                <NumberInputField />
                <NumberInputStepper>
                  <NumberIncrementStepper />
                  <NumberDecrementStepper />
                </NumberInputStepper>
              </NumberInput>
              <Text fontSize="xs" color="gray.400" mt={1}>
                {formData.priceType === 'per_court_per_month' 
                  ? 'Number of courts for pricing calculation' 
                  : 'Number of courts available (0 for unlimited or N/A)'}
              </Text>
            </FormControl>

            <FormControl>
              <FormLabel>Description</FormLabel>
              <Textarea
                value={formData.description || ''}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Package description"
                rows={2}
              />
            </FormControl>

            <FormControl>
              <FormLabel>Features (one per line)</FormLabel>
              <Textarea
                value={features}
                onChange={(e) => setFeatures(e.target.value)}
                placeholder="Enter features, one per line"
                rows={4}
              />
            </FormControl>


          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onCancel}>
            Cancel
          </Button>
          <Button colorScheme="blue" onClick={handleSave}>
            Save
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}

// Member Edit Modal Component
function MemberEditModal({ member, packages, onSave, onCancel }: {
  member: Member
  packages: PricingPackage[]
  onSave: (member: Member) => void
  onCancel: () => void
}) {
  const [formData, setFormData] = useState<Member>(member)

  const handleSave = () => {
    onSave(formData)
  }

  return (
    <Modal isOpen={true} onClose={onCancel} size="lg">
      <ModalOverlay />
      <ModalContent bg="#191919" color="white">
        <ModalHeader>Edit Member</ModalHeader>
        <ModalBody>
          <VStack spacing={4}>
            <FormControl>
              <FormLabel>Name</FormLabel>
              <Input
                value={formData.name || ''}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Member name"
              />
            </FormControl>

            <FormControl>
              <FormLabel>Email</FormLabel>
              <Input
                value={formData.email || ''}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="Member email"
              />
            </FormControl>

            <FormControl>
              <FormLabel>Package</FormLabel>
              <Input
                value={formData.package || ''}
                onChange={(e) => setFormData({ ...formData, package: e.target.value })}
                placeholder="Package type"
              />
            </FormControl>

            <FormControl>
              <FormLabel>Status</FormLabel>
              <Input
                value={formData.status || ''}
                onChange={(e) => setFormData({ ...formData, status: e.target.value as any })}
                placeholder="active, inactive, or pending"
              />
            </FormControl>

            <FormControl>
              <FormLabel>Join Date</FormLabel>
              <Input
                value={formData.joinDate || ''}
                onChange={(e) => setFormData({ ...formData, joinDate: e.target.value })}
                placeholder="YYYY-MM-DD"
              />
            </FormControl>

            <FormControl>
              <FormLabel>Last Active</FormLabel>
              <Input
                value={formData.lastActive || ''}
                onChange={(e) => setFormData({ ...formData, lastActive: e.target.value })}
                placeholder="YYYY-MM-DD"
              />
            </FormControl>

            <FormControl>
              <FormLabel>Courts (for club members)</FormLabel>
              <NumberInput value={formData.courts || 0} onChange={(_, value) => setFormData({ ...formData, courts: value })}>
                <NumberInputField />
                <NumberInputStepper>
                  <NumberIncrementStepper />
                  <NumberDecrementStepper />
                </NumberInputStepper>
              </NumberInput>
            </FormControl>
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onCancel}>
            Cancel
          </Button>
          <Button colorScheme="blue" onClick={handleSave}>
            Save
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}

// Promo Code Edit Modal Component
function PromoCodeEditModal({ promoCode, onSave, onCancel }: {
  promoCode: PromoCode
  onSave: (promo: PromoCode) => void
  onCancel: () => void
}) {
  const [formData, setFormData] = useState<PromoCode>({
    ...promoCode,
    monthlyPayments: promoCode.monthlyPayments ?? 0
  })

  const handleSave = () => {
    onSave({
      ...formData,
      code: formData.code.toUpperCase(),
      isActive: formData.isActive ?? true,
      usedCount: formData.usedCount ?? 0,
      monthlyPayments: formData.monthlyPayments ?? 0
    })
  }

  return (
    <Modal isOpen={true} onClose={onCancel} size="lg">
      <ModalOverlay />
      <ModalContent bg="#191919" color="white">
        <ModalHeader>Edit Promo Code</ModalHeader>
        <ModalBody>
          <VStack spacing={4}>
            <FormControl isRequired>
              <FormLabel>Promo Code</FormLabel>
              <Input
                value={formData.code || ''}
                onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                placeholder="Enter promo code (e.g., SAVE20)"
                bg="#2d2d2d"
                borderColor="gray.600"
                color="white"
                _placeholder={{ color: 'gray.400' }}
                _focus={{ borderColor: '#3182ce', boxShadow: 'none' }}
              />
            </FormControl>

            <FormControl isRequired>
              <FormLabel>Discount Percentage</FormLabel>
              <NumberInput
                value={formData.discountPercentage || 0}
                onChange={(_, value) => setFormData({ ...formData, discountPercentage: value })}
                min={1}
                max={100}
              >
                <NumberInputField bg="#2d2d2d" borderColor="gray.600" color="white" />
                <NumberInputStepper>
                  <NumberIncrementStepper />
                  <NumberDecrementStepper />
                </NumberInputStepper>
              </NumberInput>
            </FormControl>

            <FormControl>
              <FormLabel>Maximum Uses (Optional)</FormLabel>
              <NumberInput
                value={formData.maxUses || 0}
                onChange={(_, value) => setFormData({ ...formData, maxUses: value || undefined })}
                min={0}
              >
                <NumberInputField bg="#2d2d2d" borderColor="gray.600" color="white" />
                <NumberInputStepper>
                  <NumberIncrementStepper />
                  <NumberDecrementStepper />
                </NumberInputStepper>
              </NumberInput>
            </FormControl>

            <FormControl isRequired>
              <FormLabel>Monthly Payments</FormLabel>
              <NumberInput
                value={formData.monthlyPayments ?? 0}
                onChange={(_, value) => setFormData({ ...formData, monthlyPayments: value })}
                min={0}
              >
                <NumberInputField bg="#2d2d2d" borderColor="gray.600" color="white" />
                <NumberInputStepper>
                  <NumberIncrementStepper />
                  <NumberDecrementStepper />
                </NumberInputStepper>
              </NumberInput>
              <Text fontSize="sm" color="gray.400" mt={1}>
                0 = unlimited, 1 = first month only, 12 = first year only
              </Text>
            </FormControl>

            <FormControl>
              <FormLabel>Active</FormLabel>
              <Switch
                isChecked={formData.isActive ?? true}
                onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                colorScheme="green"
              />
            </FormControl>
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onCancel}>
            Cancel
          </Button>
          <Button colorScheme="blue" onClick={handleSave}>
            Save
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}

// Admin Login Modal Component
interface AdminLoginModalProps {
  isOpen: boolean
  onClose: () => void
  onSuccess: () => void
}

function AdminLoginModal({ isOpen, onClose, onSuccess }: AdminLoginModalProps) {
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [attempts, setAttempts] = useState(0)
  const toast = useToast()

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsLoading(true)

    try {
      const result = await secureAdminService.login(username, password)
      if (result.success) {
        toast({
          title: 'Access granted',
          description: 'Welcome, Admin',
          status: 'success',
          duration: 2000,
          isClosable: true,
        })
        onSuccess()
        setUsername('')
        setPassword('')
        setAttempts(0)
      }
    } catch (error: any) {
      const newAttempts = attempts + 1
      setAttempts(newAttempts)
      if (newAttempts >= 3) {
        toast({
          title: 'Access denied',
          description: 'Too many failed attempts. Please try again later.',
          status: 'error',
          duration: 5000,
          isClosable: true,
        })
        setTimeout(() => setAttempts(0), 30000)
      } else {
        toast({
          title: 'Invalid credentials',
          description: `Failed attempt ${newAttempts}/3`,
          status: 'error',
          duration: 3000,
          isClosable: true,
        })
      }
    }
    setIsLoading(false)
  }

  const isDisabled = attempts >= 3
  if (!isOpen) return null

  return (
    <Box
      position="fixed"
      top={0}
      left={0}
      right={0}
      bottom={0}
      bg="rgba(0, 0, 0, 0.8)"
      zIndex={9999}
      display="flex"
      alignItems="center"
      justifyContent="center"
      p={4}
    >
      <Box
        bg="#191919"
        borderRadius="xl"
        p={8}
        maxW="400px"
        w="full"
        border="1px solid"
        borderColor="gray.600"
      >
        <VStack spacing={6}>
          <VStack spacing={2}>
            <Heading size="lg" color="white">
              Admin Access
            </Heading>
            <Text fontSize="sm" color="gray.400" textAlign="center">
              Enter admin credentials to continue
            </Text>
          </VStack>
          {attempts >= 3 && (
            <Alert status="error" borderRadius="md">
              <AlertIcon />
              <Text fontSize="sm">
                Too many failed attempts. Please wait 30 seconds before trying again.
              </Text>
            </Alert>
          )}
          <VStack as="form" onSubmit={handleSubmit} spacing={4} w="full">
            <FormControl isRequired>
              <FormLabel color="gray.300">Username</FormLabel>
              <Input
                type="email"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="Enter admin username"
                bg="#2d2d2d"
                borderColor="gray.600"
                color="white"
                _placeholder={{ color: 'gray.400' }}
                _focus={{ borderColor: '#3182ce', boxShadow: 'none' }}
                isDisabled={isDisabled}
              />
            </FormControl>
            <FormControl isRequired>
              <FormLabel color="gray.300">Password</FormLabel>
              <InputGroup>
                <Input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter admin password"
                  bg="#2d2d2d"
                  borderColor="gray.600"
                  color="white"
                  _placeholder={{ color: 'gray.400' }}
                  _focus={{ borderColor: '#3182ce', boxShadow: 'none' }}
                  isDisabled={isDisabled}
                />
                <InputRightElement>
                  <IconButton
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                    icon={<Icon as={showPassword ? FaEyeSlash : FaEye} />}
                    onClick={() => setShowPassword(!showPassword)}
                    variant="ghost"
                    size="sm"
                    color="gray.400"
                    _hover={{ color: 'white' }}
                    isDisabled={isDisabled}
                  />
                </InputRightElement>
              </InputGroup>
            </FormControl>
            <HStack spacing={4} w="full" pt={2}>
              <Button
                variant="ghost"
                onClick={onClose}
                color="gray.400"
                _hover={{ color: 'white' }}
                flex={1}
                isDisabled={isLoading}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                colorScheme="blue"
                isLoading={isLoading}
                loadingText="Verifying..."
                flex={1}
                isDisabled={isDisabled}
              >
                Login
              </Button>
            </HStack>
          </VStack>
        </VStack>
      </Box>
    </Box>
  )
} 